import { useGetLogDataQuery } from "@/app/features/employee/performance/performance.api";
import CustomDrawer from "@/components/CustomDrawer";
import WithLoader from "@/components/WithLoader";
import { cn } from "@/lib/cn";
import { formatDate } from "@/utils";
import dayjs from "dayjs";


import { Divider } from "antd";

// ──────── Types ────────
interface LogEntry {
  id: string;
  title: string;
  dateTime: string;
  completed: boolean;
}

interface LogViewerProps {
  open: boolean;
  onClose: () => void;
  id?: string;
  planName?: string;
  dateTime?: string;
  logs?: LogEntry[];
}

// ──────── Mapping ────────
const LOG_TITLE_MAP: Record<string, string> = {
  KPI_OPENED: "KPI Opened by",
  KPI_SUBMITTED: "KPI Submitted by",
  KPI_APPROVED: "KPI Approval Completed by",
  KPI_RETURNED: "KPI Returned by",
  APPRAISAL_OPENED: "Appraisal Opened by",
  APPRAISAL_SUBMITTED: "Appraisal Submitted by",
  APPRAISAL_APPROVED: "Appraisal Approval Completed by",
  APPRAISAL_RETURNED: "Appraisal Returned by",
  L1_MANAGER_KPI_APPROVED: "KPI Approved by L1 Manager",
  L1_MANAGER_APPRAISAL_APPROVED: "Appraisal Approved by L1 Manager",
  L2_MANAGER_KPI_APPROVED: "KPI Approved by L2 Manager",
  L2_MANAGER_APPRAISAL_APPROVED: "Appraisal Approved by L2 Manager",
  L3_MANAGER_KPI_APPROVED: "KPI Approved by L3 Manager",
  L3_MANAGER_APPRAISAL_APPROVED: "Appraisal Approved by L3 Manager",
  L4_MANAGER_KPI_APPROVED: "KPI Approved by L4 Manager",
  L4_MANAGER_APPRAISAL_APPROVED: "Appraisal Approved by L4 Manager",
  L5_MANAGER_KPI_APPROVED: "KPI Approved by L5 Manager",
  L5_MANAGER_APPRAISAL_APPROVED: "Appraisal Approved by L5 Manager",
};

// ──────── Timeline Dot ────────
const TimelineDot = ({ completed }: { completed: boolean }) => (
  <div
    className={cn(
      "w-4 h-4 rounded-full border-2 shrink-0 ml-2",
      !completed && "bg-transparent border-theme-primary",
      completed && "bg-theme-primary border-theme-primary",
    )}
  />
);

// ──────── Timeline Connector ────────
const TimelineConnector = () => (
  <div className="w-px h-full border-l-2 border-dashed border-theme-primary ml-[7px]" />
);

// ──────── Component ────────
export default function LogViewer({
  open,
  onClose,
  id,
  planName: propsPlanName,
  dateTime: propsDateTime,
  logs: propsLogs,
}: LogViewerProps) {
  const { data: apiResponse, isFetching } = useGetLogDataQuery(id || "", {
    skip: !id || !open,
  });

  // Transform API response object to LogEntry array (Only performed)
  const transformedLogs: LogEntry[] = apiResponse?.data
    ? Object.entries(apiResponse.data)
        .filter(([_, value]: [string, any]) => value.performed)
        .sort((a, b) => {
          const timeA = dayjs((a[1] as any).performedTime).valueOf();
          const timeB = dayjs((b[1] as any).performedTime).valueOf();
          return timeB - timeA;
        })
        .map(([key, value]: [string, any]) => {
          const mappedTitle = LOG_TITLE_MAP[key] || key;
          const hasBySuffix = mappedTitle.endsWith("by");

          let title = mappedTitle;
          if (value.performedBy) {
            title = hasBySuffix
              ? `${mappedTitle} ${value.performedBy}`
              : `${mappedTitle} - ${value.performedBy}`;
          } else if (hasBySuffix) {
            title = mappedTitle.slice(0, -3); // Remove " by"
          }

          return {
            id: key,
            title,
            dateTime: value.performedTime
              ? formatDate(value.performedTime, "DD MMM, YYYY-hh:mm A")
              : "N/A",
            completed: value.performed,
          };
        })
    : [];

  const logs = propsLogs || transformedLogs;
  const planName = propsPlanName || "KPI Performance Log";
  const dateTime =
    propsDateTime || logs.find((l) => l.completed)?.dateTime || "N/A";

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={onClose}
      title={`${planName || "Log View"}`}
    >
      <WithLoader
        isLoading={isFetching}
        className={cn("rounded-lg", isFetching && "h-[calc(100vh_-_105px)]")}
      >
        {/* Header Info */}
        <div className="grid grid-cols-2 gap-6 pt-3">
          <div>
            <span className="text-theme-gray-400 text-sm block mb-1">Goal</span>
            <span className="text-white text-base font-semibold">
              {planName}
            </span>
          </div>
          <div>
            <span className="text-theme-gray-400 text-sm block mb-1">
              Last Activity
            </span>
            <span className="text-white text-base font-semibold">
              {dateTime}
            </span>
          </div>
        </div>
        <Divider />
        {/* Timeline */}
        <div className="space-y-0">
          {(logs || []).map((log, index) => {
            const isLast = index === (logs?.length || 0) - 1;

            return (
              <div key={log.id} className="flex gap-5">
                {/* Left: Dot + Connector */}
                <div className="flex flex-col items-center">
                  <TimelineDot completed={log.completed} />
                  {!isLast && (
                    <div className="flex-1 min-h-[48px]">
                      <TimelineConnector />
                    </div>
                  )}
                </div>

                {/* Right: Content */}
                <div className="pb-8">
                  <h4
                    className={`text-base font-semibold leading-tight ${
                      log.completed ? "text-white" : "text-theme-gray-400"
                    }`}
                  >
                    {log.title}
                  </h4>
                  <span className="text-theme-gray-400 text-sm mt-1 block">
                    {log.dateTime}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </WithLoader>
    </CustomDrawer>
  );
}
